import { ContentType } from "../store/models/ContentType";
import { HttpMethodType } from "../store/models/HttpMethodType";

/**
 * Initialises the HTTP request options e.g the method, headers and body if required.
 * @param httpMethod The HTTP type e.g. GET, PUT etc.
 * @param body The body of the HTTP request if required.
 */
export function createRequestOptions(httpMethod: string = HttpMethodType.Get, body?: string): RequestInit {
    const requestOptions: RequestInit = {
        method: httpMethod,
        headers: { "Content-Type": ContentType.JSON }
    };

    // Only add body if required.
    if (body) {
        requestOptions.body = body;
    }

    return requestOptions;
}

/**
 * Displays the unhandled error in the console.
 * @param error The error to handle.
 */
export function logFetchError(error: any): void {
    console.error(`Unhandled fetch error: ${error}`);
}

/**
 * Logs the error from the http response.
 * @param url The url of the api requested.
 * @param response The http response.
 * @param responseBody The http response body.
 */
export function logFetchFailure(url: string, response: Response, responseBody: Promise<any>): void {
    let errorMessage = `${response.statusText} (${response.status}). url: ${url}. Detail: ${JSON.stringify(responseBody)}`;

    console.error("ERROR: ", response);
    console.log(errorMessage);
}

/**
 * Gets the DateTime object formatted as a string in the locale format.
 * @param datetime The DateTime from the server.
 */
export function getLocaleDateTime(datetime: any): string {
    let localDateTime = new Date(datetime);

    return `${localDateTime.toLocaleDateString()} ${localDateTime.toLocaleTimeString()}`;
}

/**
 * Gets the DateTime object formatted as a date string in the locale format.
 * @param datetime The DateTime from the server.
 */
export function getLocaleDate(datetime: any): string {
    let localDateTime = new Date(datetime);

    return `${localDateTime.toLocaleDateString()}`;
}

/**
 * Returns the date in the format dd/mm/yyyy.
 * @param datetimeAsLocalTime The date to be formatted (as local time as opposed to UTC).
 */
export function getAustraliaDateFormat(datetimeAsLocalTime: any): string {
    // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
    const dateTimeFormat = new Intl.DateTimeFormat("en-AU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });

    return dateTimeFormat.format(new Date(datetimeAsLocalTime));
}

/**
 * Builds the inputs css class using the parameters passed in to determine whether to append custom css
 * and validity css based on the input's current state.
 * @param touched If the input has been updated. If not then validity css classes will not be applied.
 * @param inputClass Custom css applied by the consumer of the input control.
 * @param validityClass Css class to mark whether the input is valid/invalid i.e. if it is a required field it must have a value.
 */
export function getInputClassName(touched: boolean, inputClass?: string, validityClass?: string): string {
    // Default Bootstrap styling.
    let cssClass = `form-control`;

    if (inputClass) {
        // Custom css applied from the consumer of the input control.
        cssClass = `${cssClass} ${inputClass}`;
    }

    if (validityClass && touched) {
        // Append the validity css class. Don't apply if the field hasn't been touched i.e. when resetting the field.
        cssClass = `${cssClass} ${validityClass}`;
    }

    return cssClass;
}